<template>
  <div class="contents shinsei">
    <h2>申請</h2>
    <div class="container">
      <template v-if="isLoading">
        <loading :isLoading="isLoading" :message="'読み込み中...'"></loading>
      </template>
      <template v-else>
        <div class="container-info">
          <div class="container-base">
            <!-- <p class="danger-text" style="max-width: 300px;">
              雇用契約ID：{{ form.koyou_keiyaku_id }}<br>
              就業場所ID：{{ form.mst_syuugyou_basyo_id }}<br>
              申請雇用契約時間：{{ form.shinsei_koyou_keiyaku_time }}
            </p> -->

            <div class="col-item"><b class="title">申請日</b><span>{{ (form.shinsei_date ?? '') }}</span></div>
            <div class="col-item"><b class="title">社員番号</b><span>{{ user?.staff_code }}</span></div>
            <div class="col-item"><b class="title">氏名</b><span>{{ user?.staff_name }}</span></div>
            <div class="maininfo">
              <component v-if="isInit" :is="form.shinsei_type_str" :form="form"
                :selectSyuugyouBasyo="selectedSyuugyouBasyoWithKoyouKeiyakuOption" :targetDate="targetDate" ref="child"
                @changeKoyouKeiyakuTime="changeKoyouKeiyakuTime" />
            </div>

            <div>
              <h3>申請経路</h3>
              <scroll-table class="route-list">
                <thead>
                  <th></th>
                  <th>氏名</th>
                  <th>承認結果</th>
                  <th>コメント</th>
                  <th>日時</th>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in flow" :key="index">
                    <td v-show="item.is_syounin">承認者</td>
                    <td v-show="!item.is_syounin">確認者</td>
                    <td>{{ item.staff_name }}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                </tbody>
              </scroll-table>
            </div>
          </div>
        </div>
      </template>

      <div class="button-container">
        <p class="prev-page">
          <a @click="router.go(-1)">戻る
          </a>
        </p>
        <p class="btn-temporary-save">
          <button type="button" @click="onSave" :disabled="isLoading || isCreating || isUpdateing">
            <template v-if="isCreating">
              <i class="fa-solid fa-spinner fa-spin-pulse"></i>処理中…
            </template>
            <template v-else>
              <i class="fa-solid fa-floppy-disk"></i>{{ btnName }}
            </template>
          </button>
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed, nextTick } from "vue";
import { useInit } from "@/composable/shinsei/typelist/init";
import { useForm } from "@/composable/shinsei/typelist/form";
import { useCreate } from "@/composable/shinsei/create";
import { useUpdate } from "@/composable/shinsei/update";
import { dateFormat } from "@/filters/common";
import { useRouter } from 'vue-router';
import { RouterConst } from "@/const/Router";
import Jikangai from "../create/parts/Jikangai.vue";
import Furikyuu from "../create/parts/Furikyuu.vue";
import Yuukyuu from "../create/parts/Yuukyuu.vue";
import Kekkin from "../create/parts/Kekkin.vue";
import Soutai from "../create/parts/Soutai.vue";
import Chikoku from "../create/parts/Chikoku.vue";
import Kibiki from "../create/parts/Kibiki.vue";
import Idou from "../create/parts/Idou.vue";
import Kosyoku from "../create/parts/Kosyoku.vue";
import Nittyoku from "./parts/Nittyoku.vue";
import Kiken from "../create/parts/Kiken.vue";
import Sonota from "../create/parts/Sonota.vue";
import DakokuSyuusei from "../create/parts/DakokuSyuusei.vue";
import KyuukeiJikangai from "./parts/KyuukeiJikangai.vue";
import OptionConst from "@/const/Option";
import { useSyuugyouBasyoInit } from "@/composable/syuubyoubasyo/init";
import { useConfigStore } from "@/store/config";
import { SyuugyouBasyoAndKoyouKeiyakuOption } from "@/types/SyuugyouBasyo";
import { Logger } from "@/utils/Logger";
import { ShinseiKoyouKeiyakuTime } from "@/types/Shinsei";

export default defineComponent({
  setup() {
    // 初期化
    const { form, id, type, name, setShinseiType } = useInit()
    const { isLoading, isError, getApprover, getUser, getShinseiInfo, flow, user } = useForm()
    const { selectedSyuugyouBasyoWithKoyouKeiyakuOption, syuugyouBasyoWithKoyouKeiyakuOptions } = useSyuugyouBasyoInit()
    const router = useRouter()
    const { isCreating, create } = useCreate()
    const { isUpdateing, update } = useUpdate()
    const configStore = useConfigStore()

    const isEdit = name != RouterConst.SHINSEI_CREATE ? true : false
    const child = ref()
    const targetDate = ref();

    // システム日付を取得
    configStore.getSysDatetime()

    const isInit = ref(false)

    /** 初期処理 */
    onMounted(async () => {
      Logger.debug('----------------------------\n申請コンポーネントの初期化開始')
      if (id) {
        // IDが指定されている（＝編集モード）場合、申請情報を取得
        const res = await getShinseiInfo(form, id)

        form.value = res.data
        form.value.shinsei_type_str = res.shinsei_type
        Logger.debug('申請情報を取得しました', form.value)

        // TODO: 編集時に雇用契約が取得されない場合ここでなんとかする
      }

      // オプションの取得
      // getShinseiOptions()

      // 承認者／確認者の取得
      const shinseiType = setShinseiType(type)
      await getApprover(shinseiType)

      // ユーザー情報取得
      await getUser()

      nextTick(() => {
        // 非入力項目の「申請日」※をセット
        // ※「申請日」は「申請の対象日」ではなく「申請を提出した日」
        form.value.shinsei_date = configStore.sysDatetime ? configStore.sysDatetime.toLocaleDateString() : new Date().toLocaleDateString()
        isInit.value = true
        Logger.debug('申請コンポーネントの初期化完了\n----------------------------')
      })
    })

    // 子以下で変更された就業場所／就業時間に関する情報を申請情報にセット
    const changeKoyouKeiyakuTime = async (selectedSyuugyouBasyo: SyuugyouBasyoAndKoyouKeiyakuOption | null, shinseiKoyouKeiyakuTime: ShinseiKoyouKeiyakuTime | null) => {
      Logger.debug('[emit受信] 就業場所／就業時間の変更検知：', { '就業場所': selectedSyuugyouBasyo?.label + '（' + selectedSyuugyouBasyo?.val + '）', '雇用契約ID': selectedSyuugyouBasyo?.koyou_id, '就業時間': shinseiKoyouKeiyakuTime })
      if (selectedSyuugyouBasyo) {
        form.value.koyou_keiyaku_id = selectedSyuugyouBasyo.koyou_id
        form.value.mst_syuugyou_basyo_id = selectedSyuugyouBasyo.val
        selectedSyuugyouBasyoWithKoyouKeiyakuOption.value = selectedSyuugyouBasyo
        Logger.debug('雇用契約IDと就業場所IDをセットしました', selectedSyuugyouBasyoWithKoyouKeiyakuOption.value)
      } else {
        form.value.koyou_keiyaku_id = null
        form.value.mst_syuugyou_basyo_id = null
        selectedSyuugyouBasyoWithKoyouKeiyakuOption.value = null
        Logger.debug('就業場所が選択されていないため雇用契約IDと就業場所IDにnullをセットしました')
      }

      if (shinseiKoyouKeiyakuTime) {
        form.value.shinsei_koyou_keiyaku_time = [shinseiKoyouKeiyakuTime]
        Logger.debug('申請雇用契約時間をセットしました', form.value.shinsei_koyou_keiyaku_time)
      } else {
        form.value.shinsei_koyou_keiyaku_time = []
        Logger.debug('就業時間が選択されていないため申請雇用契約時間をにnullをセットしました')
      }
    }

    const btnName = computed(() => {
      if (!isEdit) {
        return '申請'
      }
      if (name == RouterConst.SHINSEI_EDIT) {
        return '修正'
      }
      if (name == RouterConst.SHINSEI_AGAIN) {
        return '再申請'
      }
      return ''
    })

    const onSave = async () => {
      let isSave = true
      // 有給だった場合、アラートの確認
      if (type == OptionConst.SHINSEI_TYPE.YUUKYUU) {
        isSave = await child.value.alertZanYuukyuu()
      }
      if (type == OptionConst.SHINSEI_TYPE.JIKANGAI) {
        if (isEdit) {
          // await child.value.setWorkTime()
        }
        isSave = await child.value.alertStartTime()
      }
      if (isSave) {
        save()
      }
    }

    const save = () => {
      if (isEdit) {
        saveEdit()
      } else {
        saveNew()
      }
    }

    /** 新規登録処理 */
    const saveNew = () => {
      create(form).then(res => {
        if (!res) {
          return false
        }

        if (res.shinsei_type === OptionConst.SHINSEI_CODE.JIKANGAI ||
          res.shinsei_type === OptionConst.SHINSEI_CODE.KYUUKEI_JIKANGAI) {
          router.push({ name: RouterConst.ZANGYOU_LIST });
        } else {
          const id = res.shinsei_id;
          router.push({ name: RouterConst.SHINSEI_DETAIL, params: { id: id } });
        }

      })
    };

    /** 更新処理 */
    const saveEdit = () => {
      update(form).then(res => {
        if (!res) {
          return false
        }
        const id = res
        router.push({ name: RouterConst.SHINSEI_DETAIL, params: { id: id } })
      })
    };


    return {
      form,
      isLoading,
      isCreating,
      isError,
      dateFormat,
      user,
      flow,
      id,
      type,
      RouterConst,
      isUpdateing,
      isEdit,
      selectedSyuugyouBasyoWithKoyouKeiyakuOption,
      syuugyouBasyoWithKoyouKeiyakuOptions,
      onSave,
      btnName,
      OptionConst,
      child,
      router,
      changeKoyouKeiyakuTime,
      targetDate,
      isInit
    };
  },
  components: {
    jikangai: Jikangai,
    furikyuu: Furikyuu,
    yuukyuu: Yuukyuu,
    kekkin: Kekkin,
    soutai: Soutai,
    chikoku: Chikoku,
    kibiki: Kibiki,
    idou: Idou,
    kosyoku: Kosyoku,
    nittyoku: Nittyoku,
    kiken: Kiken,
    sonotateate: Sonota,
    dakoku_syuusei: DakokuSyuusei,
    kyuukei_jikangai: KyuukeiJikangai,
  },
});
</script>

<style lang="scss" scoped>
@import '@scss/components/shinsei-create.scss';
</style>
